import { IPreferredStaffEntry, IPreferredStaffItem, PreferredStaffStatus } from "@/api_framework/api_modals/Configuration";
import { useClinicDoctorListAPI } from "@/app/hooks/api_hooks/configuration/useClinicDoctorListAPI";
import { usePreferredStaffSaveAPI } from "@/app/hooks/api_hooks/configuration/usePreferredStaffSaveAPI";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { MdDragIndicator } from "react-icons/md";
import Select, { SingleValue } from "react-select";
import ShortToggle from "@/app/components/ui/Toggles/ShortToggle";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type ClinicItem = {
  clinic_id: string;
  clinic_name: string;
  city?: string;
};

type Option = { value: string; label: string };

// react-select needs to portal its menu to the body so the modal's rounded
// panel can't clip the dropdown; z-index tops the dialog overlay (z-40).
const selectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any) => ({ ...base, minHeight: 40, fontSize: 14, borderRadius: 8, borderColor: "#e2e8f0" }),
  menu: (base: any) => ({ ...base, fontSize: 14 }),
};

interface PreferredDoctorModalProps {
  open: boolean;
  mode: "create" | "edit";
  clinicList: ClinicItem[];
  initial: IPreferredStaffItem | null; // pre-filled clinic doc when editing
  onClose: () => void;
  onSaved: () => void;
}

// A single draggable row in the ranked list. Drag reorders priority; the grip
// is the drag handle so the status toggle stays clickable.
const SortableDoctorItem = ({
  staff,
  index,
  onToggleStatus,
}: {
  staff: IPreferredStaffEntry;
  index: number;
  onToggleStatus: (id: string) => void;
}) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: staff.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-xl border bg-white px-2 py-2 shadow-sm ${isDragging ? "border-slate-900 shadow-md" : "border-slate-200"
        }`}
    >
      <button
        type="button"
        className="flex-shrink-0 cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <MdDragIndicator className="h-5 w-5" />
      </button>
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
        {index + 1}
      </span>
      <span
        className={`flex-1 truncate text-sm ${staff.status === "Inactive" ? "text-slate-400 line-through" : "text-slate-900"
          }`}
      >
        {staff.doctor_name || staff.id}
      </span>
      <div className="flex flex-shrink-0 items-center gap-1.5" title="Toggle active / inactive">
        <span
          className={`w-14 text-right text-xs font-medium ${staff.status === "Active" ? "text-emerald-600" : "text-slate-400"
            }`}
        >
          {staff.status}
        </span>
        <ShortToggle
          toggleState={staff.status === "Active"}
          onToggleChange={() => onToggleStatus(staff.id)}
        />
      </div>
    </li>
  );
};

const PreferredDoctorModal = ({ open, mode, clinicList, initial, onClose, onSaved }: PreferredDoctorModalProps) => {
  const { execute: saveMapping } = usePreferredStaffSaveAPI();
  const { execute: fetchDoctors, doctorList } = useClinicDoctorListAPI();

  const [clinicId, setClinicId] = useState("");
  // Ordered list of preferred doctors — array position is the rank (p1/p2/p3).
  const [staffs, setStaffs] = useState<IPreferredStaffEntry[]>([]);
  const [saving, setSaving] = useState(false);

  const isEdit = mode === "edit";

  // Seed the form from the clinic doc being edited / added.
  useEffect(() => {
    setClinicId(initial?.clinic_id ?? "");
    setStaffs(initial?.staffs ?? []);
  }, [initial, open]);

  // Load the clinic's doctors whenever the clinic is known (both modes).
  useEffect(() => {
    if (clinicId) fetchDoctors(clinicId);
  }, [clinicId, fetchDoctors]);

  // Searchable clinic options for the picker (create mode only).
  const clinicOptions = useMemo<Option[]>(
    () => clinicList.map((c) => ({ value: c.clinic_id, label: c.city ? `${c.clinic_name} — ${c.city}` : c.clinic_name })),
    [clinicList],
  );

  // The preferred list is exactly the clinic's doctors — no manual add/remove.
  // Status is seeded from the saved doc (`initial`) so edits keep their Active
  // doctors, then overlaid with any in-session toggles; the rest default to
  // Inactive. Seeding from `initial` (not just `prev`) matters because the
  // first run fires with an empty doctorList and would otherwise wipe prev.
  useEffect(() => {
    setStaffs((prev) => {
      const statusById = new Map<string, PreferredStaffStatus>();
      (initial?.staffs ?? []).forEach((s) => statusById.set(s.id, s.status));
      prev.forEach((s) => statusById.set(s.id, s.status));
      return doctorList.map<IPreferredStaffEntry>((d) => ({
        id: d.staff_id,
        status: statusById.get(d.staff_id) ?? "Inactive",
        doctor_name: d.name,
      }));
    });
  }, [doctorList, initial]);

  const toggleStatus = (staffId: string) =>
    setStaffs((prev) =>
      prev.map((s) => (s.id === staffId ? { ...s, status: s.status === "Active" ? "Inactive" : "Active" } : s)),
    );

  // Drag-and-drop reorder — array position is the rank, so reordering the list
  // is all that's needed (priority is derived on save from the order).
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStaffs((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const clinicName = clinicList.find((c) => c.clinic_id === clinicId)?.clinic_name ?? initial?.clinic_name;
  const canSubmit = Boolean(clinicId) && staffs.length > 0 && !saving;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await saveMapping(
        {
          clinic_id: clinicId,
          clinic_name: clinicName,
          staffs: staffs.map((s) => ({ id: s.id, status: s.status })),
        },
        isEdit ? "update" : "create",
      );
      onClose();
      onSaved();
    } catch {
      // toast already surfaced by the hook
    } finally {
      setSaving(false);
    }
  };

  const selectClass =
    "mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-50 disabled:text-slate-500";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-visible rounded-2xl bg-white text-left shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-slate-900">
                      {isEdit ? "Edit Preferred Doctors" : "Map Preferred Doctors"}
                    </Dialog.Title>
                    <p className="mt-0.5 text-sm text-slate-500">
                      Pick a clinic and rank the doctors — position 1 shows first.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="-mr-1 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  >
                    ✕
                  </button>
                </div>

                {/* Body — two columns on md+: pickers | ranked list */}
                <div className="grid min-h-[420px] grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                  {/* Left: clinic picker */}
                  <div className="space-y-4">
                    {/* Clinic — a searchable dropdown on create, read-only on edit (it's the key) */}
                    <div>
                      <label className="text-sm font-medium text-slate-700">Clinic</label>
                      {isEdit ? (
                        <input className={selectClass} value={clinicName ?? clinicId} disabled />
                      ) : (
                        <Select<Option>
                          className="mt-1"
                          classNamePrefix="pref-clinic"
                          placeholder="Search clinic"
                          options={clinicOptions}
                          value={clinicOptions.find((o) => o.value === clinicId) ?? null}
                          onChange={(opt: SingleValue<Option>) => setClinicId(opt?.value ?? "")}
                          styles={selectStyles}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          isClearable
                        />
                      )}
                    </div>
                  </div>

                  {/* Right: ranked list — position = priority (1 shows first) */}
                  <div className="flex flex-col">
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">Preferred Tag</label>
                      {staffs.length > 0 && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          {staffs.length} {staffs.length === 1 ? "doctor" : "doctors"} · drag to reorder
                        </span>
                      )}
                    </div>
                    {staffs.length === 0 ? (
                      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-slate-200 px-3 py-10 text-center text-sm text-slate-400">
                        No doctors mapped yet
                      </div>
                    ) : (
                      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={staffs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                          <ul className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                            {staffs.map((s, index) => (
                              <SortableDoctorItem
                                key={s.id}
                                staff={s}
                                index={index}
                                onToggleStatus={toggleStatus}
                              />
                            ))}
                          </ul>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={!canSubmit}
                    className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Mapping"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PreferredDoctorModal;
